import React from 'react'
import { SetFormSubmission, submissionService } from '@oneblink/apps-react'
import { ExecutedLookups, useAuth } from '@oneblink/apps-react'
import { FormTypes, SubmissionTypes } from '@oneblink/types'
import _cloneDeep from 'lodash.clonedeep'
import jsum from 'jsum'
import { toast } from 'react-toastify'

import { postRequest } from '../../../lib/http'

import { useAppConfig } from '../../../providers/AppConfigProvider'
import useSubmitForm from '../../../hooks/useSubmitForm'
import useCancelForm from '../../../hooks/useCancelForm'
import useSaveDraft from '../../../hooks/useSaveDraft'
import useCheckMaintenanceOnSubmit from '../../../hooks/useCheckMaintenanceOnSubmit'
import MaintenanceModal from '../../../components/MaintenanceModel'
import useAutoSaveApprovalsControlledForm from '../../../hooks/useAutoSaveApprovalsControlledForm'
import AutoSaveApprovalsControlledForm from '../../../components/AutoSaveApprovalsControlledForm'

import {
  getContactEmails,
  getContactPhones,
  populateBusinessContactsEmail,
} from '../../../lib/controlled-forms/common/contacts'
import {
  addClass,
  removeClass,
} from '../../../lib/controlled-forms/common/classes'
import {
  populateContactElements,
  beekeeperNameDetails,
  authorisedContactDetails,
  personCompletingFormDetails,
  updateCertifiedPersonElements,
} from '../../../lib/controlled-forms/beekeeper-registration/contact'
import generateLicenceChargesTable from '../../../lib/controlled-forms/beekeeper-registration/licence-charges'
import {
  findElement,
  replaceElement,
  replaceElementOptions,
  createElementOptions,
} from '../../../lib/definition-manipulation'
import isDeepEqual from '../../../lib/is-deep-equal'
import makeIsShowing from '../../../lib/is-element-showing'
import {
  setHasPricingLookupRunInitial,
  updateHasPricingLookupRun,
  verifyPricingLookupHasRun,
} from '../../../lib/controlled-forms/common/has-pricing-lookup-run'
import getPricingAndCrn from '../../../lib/controlled-forms/common/get-pricing-and-crn'
import {
  generateFacilityLineItems,
  generateApplicationFeeLineItem,
} from '../../../lib/controlled-forms/common/generate-invoice-line-items'
import {
  getPointAddressPostalDeliveryType,
  getPointAddressStateTerritory,
  SubmissionPointAddress,
} from '../../../lib/point-address'

import config from '../../../config'

type Props = { form: FormTypes.Form; preFillData: Record<string, unknown> }

function generatePartialModel(
  submission: Record<string, unknown>,
): Record<string, unknown> {
  return {
    registrationType: submission.registrationType,
    eligibleConcession: submission.eligibleConcession,
    Process: submission.Process,
  }
}

function generateHash(submission: Record<string, unknown>): string {
  return jsum.digest(
    //need to clone deep becoz jsum corrupts the model somehow
    generatePartialModel(_cloneDeep(submission)),
    'sha256',
    'hex',
  )
}

export default function BeekeerRegistrationFormContainer({
  form,
  preFillData,
}: Props) {
  const { buttons } = useAppConfig()
  const { userProfile } = useAuth()

  const [isGettingCharges, setIsGettingCharges] = React.useState<boolean>(false)

  const poBoxRegex = React.useMemo(() => /^po box/i, [])

  const initialDefinition = React.useMemo(() => {
    let def = form

    if (userProfile && userProfile.email) {
      def = populateBusinessContactsEmail({}, form, userProfile.email)

      const originalEmailElement = findElement<FormTypes.EmailElement>(
        def,
        '_Email',
      )

      if (
        originalEmailElement &&
        originalEmailElement.value &&
        originalEmailElement.parent
      ) {
        const emailElement = {
          ...originalEmailElement.value,
          defaultValue: userProfile.email,
        }

        originalEmailElement.parent.elements = replaceElement(
          originalEmailElement.parent.elements || [],
          originalEmailElement.value,
          emailElement,
        )
      }
    }

    setHasPricingLookupRunInitial(form, { buttonName: 'Calculate fees' })

    return def
  }, [form, userProfile])

  const [previousRegistrationType, setPreviousRegistrationType] =
    React.useState<string | undefined>(preFillData.registrationType as string)
  const [previousCertifiedUser, setPreviousCertifiedUser] = React.useState<
    string | undefined
  >(preFillData.personCompletingThisFormAndProvidingCertificationS as string)
  const [partialModelHash, setPartialModelHash] = React.useState<string>()

  /*
    `customSetFormSubmission` is handed to every element in the form as its
    `onChange`. Reading these two values from state would put them in its
    dependency list, so the callback would get a new identity whenever either
    changed. Derived-value elements (calculation, summary) re-run their write
    effect on a new `onChange`, and a repeatable set rebuilds the whole entry
    for any write, so a changing identity here is enough to loop the form
    forever. Mirror them into refs and keep the callback stable.
  */
  const partialModelHashRef = React.useRef(partialModelHash)
  const previousCertifiedUserRef = React.useRef(previousCertifiedUser)

  const updatePartialModelHash = React.useCallback((hash: string) => {
    partialModelHashRef.current = hash
    setPartialModelHash(hash)
  }, [])

  const updatePreviousCertifiedUser = React.useCallback(
    (certifiedUser: string | undefined) => {
      previousCertifiedUserRef.current = certifiedUser
      setPreviousCertifiedUser(certifiedUser)
    },
    [],
  )

  const [onSaveDraft, isSavingDraft] = useSaveDraft({
    formDefinition: initialDefinition,
    preFillData,
  })
  const [onCancel, cancelError] = useCancelForm(initialDefinition)
  const [
    { onSubmit, clearServerValidationMessage },
    { isSubmitting, serverValidationMessage },
  ] = useSubmitForm({
    formDefinition: initialDefinition,
    preFillData,
    skipServerValidation: false,
  })

  const {
    definition,
    liveSubmission,
    setFormSubmission,
    handleSubmit,
    handleCancel,
    handleSaveDraft,
    isAutoSaveSubmissionAvailable,
    isLoadingAutoSaveSubmission,
    startNewSubmission,
    continueAutoSaveSubmission,
    executedLookups,
  } = useAutoSaveApprovalsControlledForm({
    form: initialDefinition,
    initialSubmission: preFillData,
    onSubmit,
    onCancel,
    onSaveDraft,
  })

  /* update definition here */
  const customDefinition = React.useMemo(() => {
    let def = definition
    def = generateLicenceChargesTable(liveSubmission, def)
    def = populateContactElements(liveSubmission, def, userProfile)
    def = updateCertifiedPersonElements(liveSubmission, def, userProfile)
    const registrationType = liveSubmission.registrationType

    if (registrationType !== previousRegistrationType) {
      const businessStructureElementName = '_Business_Structure'

      const originalBusinessStructureElement = findElement(
        form,
        businessStructureElementName,
      )

      const businessStructureElement = findElement(
        def,
        businessStructureElementName,
      )

      if (
        originalBusinessStructureElement?.value &&
        originalBusinessStructureElement?.value.type === 'radio' &&
        originalBusinessStructureElement?.value.options &&
        businessStructureElement?.value &&
        businessStructureElement?.value.type === 'radio'
      ) {
        const opts =
          registrationType === 'Recreational'
            ? originalBusinessStructureElement?.value.options?.filter(
                (opt) => opt.value !== 'Company',
              )
            : originalBusinessStructureElement?.value.options

        replaceElementOptions(def, businessStructureElementName, opts, [])
      }
    }
    setPreviousRegistrationType(registrationType as string)

    const emailOpts = getContactEmails(liveSubmission, def.elements)
    const phoneOpts = getContactPhones(liveSubmission, def.elements)

    if (userProfile?.email) {
      emailOpts.add(userProfile.email)
    }

    if (userProfile?.phoneNumber) {
      phoneOpts.add(userProfile.phoneNumber)
    }

    replaceElementOptions(
      def,
      'Main_Email',
      createElementOptions(Array.from(emailOpts)),
    )

    replaceElementOptions(
      def,
      'Main_Phone',
      createElementOptions(Array.from(phoneOpts)),
    )

    const hiveAddressSameAsMainElement = findElement(
      def,
      'hiveAddressSameAsMain',
    )?.value as FormTypes.SelectElement

    if (hiveAddressSameAsMainElement) {
      hiveAddressSameAsMainElement.customCssClasses =
        liveSubmission.hideHiveAddressSameAsMain
          ? addClass('hidden', hiveAddressSameAsMainElement.customCssClasses)
          : removeClass('hidden', hiveAddressSameAsMainElement.customCssClasses)
    }

    const originalHiveLocatedElement = findElement(
      initialDefinition,
      'hiveLocated',
    )?.value as FormTypes.SelectElement

    if (
      liveSubmission.isFacilityAddressInNSW &&
      !liveSubmission.isManualHiveAddressPOBox
    ) {
      replaceElementOptions(
        def,
        'hiveLocated',
        originalHiveLocatedElement.options || [],
      )
    } else {
      replaceElementOptions(
        def,
        'hiveLocated',
        originalHiveLocatedElement.options?.filter(
          (option) => option.value !== 'Primary' && option.value !== 'Various',
        ) || [],
      )
    }

    // pages are memo'd so we need new objects for the updated definition to render
    return { ...def, elements: def.elements.map((el) => ({ ...el })) }
  }, [
    form,
    initialDefinition,
    userProfile,
    definition,
    liveSubmission,
    previousRegistrationType,
  ])

  /* update submission data here */
  const customSetFormSubmission: SetFormSubmission = React.useCallback(
    (formSubmission) => {
      setFormSubmission((currentState) => {
        const newFormSubmission: {
          definition: FormTypes.Form
          submission: SubmissionTypes.S3SubmissionData['submission']
          lastElementUpdated: FormTypes.FormElement | undefined
          executedLookups: ExecutedLookups
        } =
          typeof formSubmission === 'function'
            ? formSubmission(currentState)
            : formSubmission

        const { submission, definition } = newFormSubmission

        const isShowing = makeIsShowing(submission, definition.elements)

        // hideHiveAddressSameAsMain
        if (submission.Manual_Address) {
          const address = ((submission.Address_1 as string) ?? '').trim()
          submission.hideHiveAddressSameAsMain = poBoxRegex.test(address)
        } else {
          const address = submission.Address as SubmissionPointAddress
          submission.hideHiveAddressSameAsMain =
            (getPointAddressPostalDeliveryType(address) || '').toLowerCase() ===
            'po box'
        }

        if (!submission.hideHiveAddressSameAsMain) {
          submission.hideHiveAddressSameAsMain =
            isShowing('AustralianAddress') &&
            submission.AustralianAddress === 'No'
        }

        // hiveAddressSameAsMain
        if (submission.hideHiveAddressSameAsMain) {
          submission.hiveAddressSameAsMain = 'No'
        }

        // isFacilityAddressInNSW
        if (submission.hiveAddressSameAsMain === 'Yes') {
          if (submission.Manual_Address) {
            if (submission.AustralianAddress === 'Yes') {
              submission.isFacilityAddressInNSW = submission.State_ === 'NSW'
            } else {
              submission.isFacilityAddressInNSW = false
            }
          } else {
            const address = submission.Address as SubmissionPointAddress
            submission.isFacilityAddressInNSW =
              getPointAddressStateTerritory(address) === 'NSW'
          }
        } else {
          if (submission.Hive_Manual_Address) {
            submission.isFacilityAddressInNSW = submission.Hive_State === 'NSW'
          } else {
            const address =
              submission.hiveAddressMaintained as SubmissionPointAddress

            submission.isFacilityAddressInNSW =
              getPointAddressStateTerritory(address) === 'NSW'
          }
        }

        if (isShowing('trainingInformation')) {
          beekeeperNameDetails(submission, definition, userProfile)
        }

        if (
          previousCertifiedUserRef.current !==
          submission.personCompletingThisFormAndProvidingCertificationS
        ) {
          console.log('setting to undefined')
          submission.Phone_Certified = undefined
          submission.Role_Certified = undefined
          updatePreviousCertifiedUser(
            submission.personCompletingThisFormAndProvidingCertificationS as string,
          )
        }
        authorisedContactDetails(submission, definition, userProfile)
        personCompletingFormDetails(submission, definition, userProfile)

        // isManualHiveAddressPOBox
        const hiveManualAddress = (
          (submission.Hive_Address_1 as string) ?? ''
        ).trim()

        submission.isManualHiveAddressPOBox =
          submission.hiveAddressSameAsMain === 'No' &&
          submission.Hive_Manual_Address &&
          poBoxRegex.test(hiveManualAddress)

        // Hive_LGA display conditions
        if (
          (submission.hiveAddressSameAsMain === 'Yes' &&
            isShowing('AustralianAddress') &&
            submission.AustralianAddress === 'Yes') ||
          (isShowing('Hive_Manual_Address') && submission.Hive_Manual_Address)
        ) {
          submission.showHiveLGAElement = true
        } else {
          submission.showHiveLGAElement = false
        }

        //always do this last
        updateHasPricingLookupRun({
          submission: submission,
          currentHash: partialModelHashRef.current,
          newHash: generateHash(submission),
          updateHash: updatePartialModelHash,
        })

        /*
          Calculation elements re-run and write their result back whenever the
          `onChange` handed to them changes identity, even when the value is
          identical. Returning a new state object for one of those writes gives
          the renderer a new definition and a new `onChange` for every element,
          which runs the calculations again, and the form never settles. Keeping
          the current state when nothing actually changed breaks that cycle.
        */
        if (
          definition === currentState.definition &&
          isDeepEqual(submission, currentState.submission)
        ) {
          return currentState
        }

        return { ...currentState, submission, definition }
      })
    },
    [
      poBoxRegex,
      userProfile,
      setFormSubmission,
      updatePartialModelHash,
      updatePreviousCertifiedUser,
    ],
  )

  const customHandleSubmission = React.useCallback(
    async (newFormSubmission: submissionService.NewFormSubmission) => {
      //taking all the precautions here and overengineering it
      const clonedNewFormSubmission = _cloneDeep(newFormSubmission)
      let pricingCompleted = false
      if (
        !verifyPricingLookupHasRun({
          newFormSubmission: clonedNewFormSubmission,
          calculateName: 'fees',
        })
      ) {
        return
      }

      setIsGettingCharges(true)

      try {
        const pricingFunc = async () =>
          postRequest<{
            facilities__hidden: FacilityWithPermissionsAndProcesses[]
            paymentTotal__hidden: number
            applicationFeeTaxRate__hidden: number
            applicationFee__hidden: number
          }>({
            url: `${config.HOSTED_API_URL}/lookup/beekeeper-registration/permissions-and-pricing`,
            resource: {
              submission: clonedNewFormSubmission.submission,
              definition: clonedNewFormSubmission.definition,
            },
          })

        const { pricingResponse, customerReferenceNumber } =
          await getPricingAndCrn(pricingFunc, 'Biosecurity')

        const facilityLineItems = generateFacilityLineItems(pricingResponse)
        const applicationFeeLineItem =
          generateApplicationFeeLineItem(pricingResponse)

        clonedNewFormSubmission.submission = {
          ...clonedNewFormSubmission.submission,
          paymentTotal__hidden: pricingResponse.paymentTotal__hidden,
          customerReferenceNumber__hidden: customerReferenceNumber,
          lineItems: [...facilityLineItems, applicationFeeLineItem],
        }
        pricingCompleted = true
      } catch (e) {
        console.error(e)
        toast.error('There was an error verifying payment amount', {
          position: 'bottom-right',
          hideProgressBar: true,
        })
      }

      setIsGettingCharges(false)

      //a Hail Mary to prevent premature submissions
      if (!pricingCompleted) {
        return
      }

      handleSubmit(clonedNewFormSubmission)
    },
    [handleSubmit],
  )

  const [
    { isFetchingMaintenanceWindow, fetchMaintenanceWindowError },
    { wrappedSubmitFn, clearMaintenanceMessage },
  ] = useCheckMaintenanceOnSubmit(customHandleSubmission)

  return (
    <>
      <AutoSaveApprovalsControlledForm
        definition={customDefinition}
        submission={liveSubmission}
        onCancel={handleCancel}
        onSubmit={wrappedSubmitFn}
        onSaveDraft={handleSaveDraft}
        setFormSubmission={customSetFormSubmission}
        buttons={buttons}
        cancelError={cancelError}
        isSubmitting={
          isSubmitting || isGettingCharges || isFetchingMaintenanceWindow
        }
        isAutoSaveSubmissionAvailable={isAutoSaveSubmissionAvailable}
        isLoadingAutoSaveSubmission={isLoadingAutoSaveSubmission}
        isSavingDraft={isSavingDraft}
        startNewSubmission={startNewSubmission}
        continueAutoSaveSubmission={continueAutoSaveSubmission}
        executedLookups={executedLookups}
        serverValidationMessage={serverValidationMessage}
        clearServerValidationMessage={clearServerValidationMessage}
      />
      <MaintenanceModal
        fetchMaintenanceWindowError={fetchMaintenanceWindowError}
        clearMaintenanceMessage={clearMaintenanceMessage}
      />
    </>
  )
}
